// frontend/src/askos/AskosChat.js
//
// New, independent chat component for the Samkalp dashboard — calls the
// existing /session/init and /chat endpoints directly with its own state.
// Deliberately NOT extracted from the 2,486-line pages/AskOS.js (per the
// confirmed Phase 1 decision) to avoid destabilizing chat for other tenants.
// No voice/image-attach in this first pass. Markup matches the .chat-card
// design in /askos-dashboard.html.
import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import api, { askosAuthHeader } from './askosApi';
import { normalizeMathForMarkdown, stripFollowUps, speakText, stopSpeaking, loadPrefs } from './prefsUtils';
import BookmarkButton from './BookmarkButton';

const MarkdownRenderers = {
    p: ({ node, ...props }) => <p className="md-p" {...props} />,
    ul: ({ node, ...props }) => <ul className="md-list" {...props} />,
    ol: ({ node, ...props }) => <ol className="md-list" {...props} />,
};

const AskosChat = ({ slug, compact = false }) => {
    const [searchParams] = useSearchParams();
    const resumeSessionId = searchParams.get('session');

    const [projects, setProjects] = useState([]);
    const [session, setSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [lastReferences, setLastReferences] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [speakingIdx, setSpeakingIdx] = useState(null);
    const bottomRef = useRef(null);
    const prefs = loadPrefs();

    const toggleReadAloud = (idx, content) => {
        if (speakingIdx === idx) {
            stopSpeaking();
            setSpeakingIdx(null);
            return;
        }
        speakText(stripFollowUps(content), prefs.language, Number(prefs.voiceSpeed) || 1.0, prefs.voiceType || 'female', prefs.voiceName || null);
        setSpeakingIdx(idx);
    };

    useEffect(() => () => stopSpeaking(), []);

    const loadProjects = () => {
        api.get(`/tenant/${slug}/askos/projects`, { headers: askosAuthHeader() })
            .then(res => setProjects(res.data || []))
            .catch(() => setProjects([]));
    };

    useEffect(() => {
        if (resumeSessionId) {
            api.get(`/tenant/${slug}/askos/session/${resumeSessionId}`, { headers: askosAuthHeader() })
                .then(res => {
                    setSession({ session_id: res.data.session_id, concept_id: res.data.concept_id });
                    setMessages(res.data.history || []);
                })
                .catch(() => setError('Could not load that conversation.'));
        } else {
            loadProjects();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, resumeSessionId]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const startSession = async (projectId) => {
        setError('');
        try {
            const res = await api.post(`/tenant/${slug}/askos/session/init`, { project_id: projectId }, { headers: askosAuthHeader() });
            setSession({ session_id: res.data.session_id, concept_id: res.data.concept_id });
            setMessages(res.data.history || []);
            setLastReferences([]);
        } catch {
            setError('Could not start a session. Please try again.');
        }
    };

    const newChat = () => {
        setSession(null);
        setMessages([]);
        setLastReferences([]);
        setError('');
        loadProjects();
    };

    const sendMessage = async () => {
        const text = input.trim();
        if (!text || loading || !session) return;
        setInput('');
        setError('');
        setMessages(prev => [...prev, { role: 'user', content: text }]);
        setLoading(true);
        try {
            const res = await api.post(`/tenant/${slug}/askos/chat`, {
                session_id: session.session_id,
                message: text,
                concept_id: session.concept_id || null,
            }, { headers: askosAuthHeader() });
            const history = res.data.history || [];
            setMessages(history);
            setLastReferences(res.data.references || []);
            if (res.data.concept_id) setSession(s => ({ ...s, concept_id: res.data.concept_id }));

            const lastMsg = history[history.length - 1];
            if (prefs.voiceEnabled && lastMsg?.role === 'assistant') {
                speakText(stripFollowUps(lastMsg.content), prefs.language, Number(prefs.voiceSpeed) || 1.0, prefs.voiceType || 'female', prefs.voiceName || null);
                setSpeakingIdx(history.length - 1);
            }
        } catch {
            setError('Something went wrong sending that message. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <div className={`chat-card${compact ? '' : ' chat-card--full'}`}>
            <div className="chat-header">
                <div className="askos-mini">Ask<span>OS</span></div>
                <span role="button" tabIndex={0} title="Start a new chat" onClick={newChat} style={{ cursor: 'pointer' }}>☰</span>
            </div>

            {!session && (
                <>
                    <h3>Hello! 👋</h3>
                    <p>How can I help you with your UPSC preparation today?</p>
                    {error && <p className="empty-note">{error}</p>}
                    <div className="subject-pick-list">
                        {projects.map(p => (
                            <button key={p.id} type="button" className="subject-pick-btn" onClick={() => startSession(p.id)}>
                                {p.name}
                            </button>
                        ))}
                        {projects.length === 0 && <p className="empty-note">No subjects available yet.</p>}
                    </div>
                </>
            )}

            {session && (
                <div className="chat-messages">
                    {messages.map((m, idx) => (
                        m.role === 'user'
                            ? <div key={idx} className="user-bubble">{m.content}</div>
                            : (
                                <div key={idx} className="ai-bubble">
                                    <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]} components={MarkdownRenderers}>
                                        {normalizeMathForMarkdown(stripFollowUps(m.content))}
                                    </ReactMarkdown>
                                    <button
                                        type="button"
                                        className="read-aloud-btn"
                                        title={speakingIdx === idx ? 'Stop reading' : 'Read aloud'}
                                        onClick={() => toggleReadAloud(idx, m.content)}
                                    >
                                        {speakingIdx === idx ? '⏹' : '🔊'}
                                    </button>
                                    {session.concept_id && (
                                        <BookmarkButton
                                            slug={slug}
                                            target={{ concept_id: session.concept_id }}
                                            note={stripFollowUps(m.content).slice(0, 200)}
                                        />
                                    )}
                                </div>
                            )
                    ))}
                    {loading && <div className="ai-bubble">Thinking…</div>}
                    {!loading && lastReferences.length > 0 && (
                        <div className="source-tags">
                            {lastReferences.map((ref, i) => (
                                <span key={i}>{ref.book}{ref.pages ? ` – ${ref.pages}` : ''}</span>
                            ))}
                        </div>
                    )}
                    {error && <p className="empty-note">{error}</p>}
                    <div ref={bottomRef} />
                </div>
            )}

            <div className="chat-input">
                <input
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask anything..."
                    disabled={!session}
                />
                <button type="button" onClick={sendMessage} disabled={loading || !input.trim() || !session}>➤</button>
            </div>
        </div>
    );
};

export default AskosChat;
